import {
	AdminActionArea,
	AdminActionGroup,
	AdminActionPriority,
	useMarkAdminActionHandledMutation,
	useRestoreAdminActionMutation,
} from '@codezo/graphql';
import { Check, Clock, Inbox, RefreshCw } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn, timeAgo } from '../../lib/utils';
import { toast } from 'sonner';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { InitialsAvatar } from '../ui/initials-avatar';
import {
	AREA_LABELS,
	GROUP_LABELS,
	GROUP_ORDER,
	type ActionItem,
	actionHref,
	dueLabel,
	itemRef,
	ownerName,
	sinceLabel,
} from './actionQueueFormat';

/* ── Priority mark: urgent = red square with "!", bars for high/medium/low, clock for client waits ── */

function PriorityMark({ priority }: { priority: AdminActionPriority }) {
	if (priority === AdminActionPriority.Urgent) {
		return (
			<svg width="14" height="14" viewBox="0 0 14 14" role="img" aria-label="Urgent">
				<rect width="14" height="14" rx="3.5" className="fill-status-danger" />
				<rect x="6.1" y="3" width="1.8" height="5" rx="0.9" className="fill-canvas" />
				<rect x="6.1" y="9.4" width="1.8" height="1.8" rx="0.9" className="fill-canvas" />
			</svg>
		);
	}
	if (priority === AdminActionPriority.Waiting) {
		return <Clock size={14} className="text-text-3" aria-label="Waiting on client" />;
	}
	const filled =
		priority === AdminActionPriority.High ? 3 : priority === AdminActionPriority.Medium ? 2 : 1;
	return (
		<svg
			width="14"
			height="14"
			viewBox="0 0 14 14"
			role="img"
			aria-label={`${priority.toLowerCase()} priority`}
		>
			{[0, 1, 2].map((i) => (
				<rect
					key={i}
					x={1.5 + i * 4}
					y={8 - i * 3}
					width="3"
					height={4.5 + i * 3}
					rx="1"
					className={i < filled ? 'fill-text-2' : 'fill-line-3'}
				/>
			))}
		</svg>
	);
}

const TONE_CLASS = {
	danger: 'text-status-danger',
	warn: 'text-status-warn',
	muted: 'text-text-3',
} as const;

function QueueRow({
	item,
	primary,
	onMarkHandled,
}: {
	item: ActionItem;
	primary: boolean;
	onMarkHandled?: (item: ActionItem) => void;
}) {
	const navigate = useNavigate();
	const due = dueLabel(item);
	const since = sinceLabel(item);
	const ref = itemRef(item);
	const owner = item.owners?.[0];
	const open = () => navigate(actionHref(item));

	return (
		<div
			className="group grid min-h-[58px] cursor-pointer grid-cols-[16px_minmax(0,1fr)_auto] items-center gap-x-3.5 border-b border-line-1 py-2 pr-3 pl-4 transition-colors hover:bg-surface-hover md:grid-cols-[16px_minmax(0,1.7fr)_minmax(140px,0.8fr)_minmax(110px,0.5fr)_88px_20px_128px]"
			onClick={open}
			role="link"
			tabIndex={0}
			onKeyDown={(e) => e.key === 'Enter' && open()}
		>
			<span className="flex h-4 w-4 items-center justify-center">
				<PriorityMark priority={item.priority} />
			</span>
			<span className="flex min-w-0 flex-col gap-0.5">
				<span className="truncate text-base font-medium text-text-1">{item.title}</span>
				{item.context && (
					<span className="truncate text-sm text-text-3">{item.context}</span>
				)}
			</span>
			<span className="hidden min-w-0 flex-col gap-0.5 md:flex">
				<span className="truncate text-base text-text-1">
					{item.organization?.name ?? '—'}
				</span>
				{ref && <span className="truncate font-mono text-xs text-text-4">{ref}</span>}
			</span>
			<span
				className={cn(
					'hidden truncate text-sm tabular-nums md:block',
					TONE_CLASS[since.tone]
				)}
			>
				{since.text}
			</span>
			<span
				className={cn(
					'hidden text-right text-sm tabular-nums md:block',
					TONE_CLASS[due.tone]
				)}
			>
				{due.text}
			</span>
			<span className="hidden md:block" title={owner ? ownerName(owner) : 'Unassigned'}>
				{owner ? <InitialsAvatar name={ownerName(owner)} size={20} /> : null}
			</span>
			<span className="flex items-center justify-end gap-1">
				{onMarkHandled && (
					<button
						type="button"
						title="Mark handled"
						aria-label={`Mark handled: ${item.title}`}
						onClick={(e) => {
							e.stopPropagation();
							onMarkHandled(item);
						}}
						className="flex size-[26px] items-center justify-center rounded-[7px] text-text-3 transition-[opacity,background-color] hover:bg-surface-fill hover:text-text-1 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
					>
						<Check size={14} />
					</button>
				)}
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						open();
					}}
					className={cn(
						'h-[26px] rounded-[7px] border px-2.5 text-xs font-medium whitespace-nowrap transition-[opacity,background-color]',
						primary
							? 'border-accent bg-accent text-accent-foreground hover:bg-accent-hover'
							: 'border-line-3 text-text-1 hover:bg-surface-hover md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100'
					)}
				>
					{item.actionLabel}
				</button>
			</span>
		</div>
	);
}

type AreaFilter = 'all' | AdminActionArea;

/**
 * "Needs a reaction": the server-sorted queue, grouped into Overdue or at risk / Needs studio action
 * / Waiting on client. Order inside a group is the server's — this component never re-sorts.
 */
export function ActionQueue({
	items,
	loading,
	error,
	onRetry,
	generatedAt,
	emptyText,
	onChanged,
}: {
	items: ActionItem[];
	loading: boolean;
	error?: boolean;
	onRetry: () => void;
	generatedAt?: string;
	/** Shown when nothing matches, before any area filter. */
	emptyText?: string;
	/** Called after an item is marked handled or restored, so the owner can refetch. */
	onChanged?: () => void;
}) {
	const [area, setArea] = useState<AreaFilter>('all');
	const [confirming, setConfirming] = useState<ActionItem | null>(null);
	const [markHandled, { loading: marking }] = useMarkAdminActionHandledMutation();
	const [restore] = useRestoreAdminActionMutation();

	const confirmHandled = async () => {
		if (!confirming) return;
		const item = confirming;
		try {
			const { data } = await markHandled({ variables: { id: item.id } });
			const res = data?.markAdminActionHandled;
			if (!res?.success) {
				toast.error(res?.message ?? 'Couldn’t mark it handled');
				return;
			}
			setConfirming(null);
			onChanged?.();
			toast.success('Marked handled', {
				description: item.title,
				action: {
					label: 'Undo',
					onClick: async () => {
						await restore({ variables: { id: item.id } });
						onChanged?.();
					},
				},
			});
		} catch {
			toast.error('Couldn’t mark it handled');
		}
	};

	const areas = useMemo(() => {
		const count = (a: AreaFilter) =>
			a === 'all' ? items.length : items.filter((i) => i.area === a).length;
		return (['all', ...Object.values(AdminActionArea)] as AreaFilter[]).map((a) => ({
			value: a,
			label: a === 'all' ? 'All' : AREA_LABELS[a],
			count: count(a),
		}));
	}, [items]);

	const groups = useMemo(() => {
		const shown = area === 'all' ? items : items.filter((i) => i.area === area);
		return GROUP_ORDER.map((group) => ({
			group,
			items: shown.filter((i) => i.group === group),
		})).filter((g) => g.items.length > 0);
	}, [items, area]);

	// The filled button goes on the first row the studio can act on — never on a client wait.
	const primaryId = groups.find((g) => g.group !== AdminActionGroup.WaitingOnClient)?.items[0]
		?.id;

	return (
		<section
			aria-labelledby="queue-title"
			className="overflow-hidden rounded-xl border border-line-1 bg-surface"
		>
			<div className="flex min-h-12 flex-wrap items-center gap-2 border-b border-line-1 px-4 py-2">
				<h2
					id="queue-title"
					className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
				>
					Needs a reaction
				</h2>
				<span className="text-sm tabular-nums text-text-4">{items.length}</span>
				<span className="flex-1" />
				<div
					role="group"
					aria-label="Filter by area"
					className="hidden rounded-[7px] border border-line-1 bg-surface-sunken p-0.5 md:inline-flex"
				>
					{areas.map((a) => (
						<button
							key={a.value}
							type="button"
							aria-pressed={area === a.value}
							onClick={() => setArea(a.value)}
							className={cn(
								'inline-flex h-[22px] items-center gap-1.5 rounded-[6px] px-2 text-xs font-medium transition-colors',
								area === a.value
									? 'bg-surface-fill text-text-1 shadow-[0_0_0_1px_var(--color-line-2)]'
									: 'text-text-3 hover:text-text-1'
							)}
						>
							{a.label}
							<span className="tabular-nums text-text-4">{a.count}</span>
						</button>
					))}
				</div>
			</div>

			{loading && items.length === 0 ? (
				<div aria-busy="true">
					{Array.from({ length: 5 }).map((_, i) => (
						<div
							key={i}
							className="flex h-[58px] items-center gap-3 border-b border-line-1 px-4"
						>
							<span className="h-3.5 w-3.5 animate-pulse rounded bg-surface-fill" />
							<span className="h-3 w-1/3 animate-pulse rounded bg-surface-fill" />
							<span className="flex-1" />
							<span className="h-3 w-16 animate-pulse rounded bg-surface-fill" />
						</div>
					))}
				</div>
			) : error && items.length === 0 ? (
				<div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
					<span className="text-base text-text-2">Couldn&rsquo;t load the queue.</span>
					<button
						type="button"
						onClick={onRetry}
						className="inline-flex items-center gap-1.5 rounded-lg border border-line-3 px-3 py-1.5 text-sm text-text-1 hover:bg-surface-hover"
					>
						<RefreshCw size={13} /> Try again
					</button>
				</div>
			) : groups.length === 0 ? (
				<div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
					<Inbox size={20} className="text-text-3" />
					<span className="text-base text-text-2">
						{area === 'all'
							? (emptyText ?? 'Nothing needs a reaction right now.')
							: 'Nothing in this area needs a reaction.'}
					</span>
				</div>
			) : (
				groups.map(({ group, items: rows }) => (
					<div key={group}>
						<div className="flex h-8 items-center gap-2 border-b border-line-1 bg-surface-hover px-4 text-sm font-medium">
							<span
								className={
									group === AdminActionGroup.OverdueOrAtRisk
										? 'text-status-danger'
										: 'text-text-2'
								}
							>
								{GROUP_LABELS[group]}
							</span>
							<span className="font-normal tabular-nums text-text-4">
								{rows.length}
							</span>
						</div>
						{rows.map((item) => (
							<QueueRow
								key={item.id}
								item={item}
								primary={item.id === primaryId}
								onMarkHandled={setConfirming}
							/>
						))}
					</div>
				))
			)}

			<ConfirmDialog
				open={!!confirming}
				onOpenChange={(open) => !open && setConfirming(null)}
				title="Mark as handled?"
				description={
					<>
						<span className="block text-text-1">{confirming?.title}</span>
						<span className="mt-1 block">
							It leaves the queue now and comes back by itself if something new
							happens — a new message, a moved date.
						</span>
					</>
				}
				confirmLabel="Mark handled"
				variant="default"
				loading={marking}
				onConfirm={confirmHandled}
			/>

			<div className="flex min-h-10 items-center gap-2 border-t border-line-1 px-4 py-2.5 text-xs text-text-4">
				<span>Overdue first, then due today, then nearest deadline.</span>
				<span className="flex-1" />
				{generatedAt && <span>Updated {timeAgo(generatedAt)}</span>}
			</div>
		</section>
	);
}
